import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/SEO/JsonLd';
import { getComicById, getSimilarComics } from '@/mocks/comicMocks';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '1');
  const [activeTab, setActiveTab] = useState<'chapters' | 'comments'>('chapters');
  const [favorited, setFavorited] = useState(false);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 px-4 md:px-6 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-600 text-sm mb-6">该漫画可能已被删除或链接有误</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const similarComics = getSimilarComics(comic.id, comic.categorySlug, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    image: comic.coverImage,
    datePublished: comic.updateDate,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
      logo: { '@type': 'ImageObject', url: 'http://knn08.com/logo.png' },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://knn08.com/comic/${comic.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://knn08.com/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: 'http://knn08.com/category/all' },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://knn08.com/comic/${comic.id}` },
    ],
  };

  const reviewSchema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'CreativeWork',
      name: comic.title,
      author: { '@type': 'Person', name: comic.author },
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: comic.rating,
      bestRating: 5,
    },
    author: { '@type': 'Organization', name: '韩漫大全用户' },
  };

  return (
    <>
      <JsonLd data={[articleSchema, breadcrumbSchema, reviewSchema]} />
      <Navbar />
      <main>
        {/* Comic Header */}
        <section className="pt-24 pb-10 bg-background-100">
          <div className="px-4 md:px-6">
            {/* Breadcrumb */}
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-600 flex-wrap">
                <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
                <li><Link to="/category/all" className="hover:text-primary-600 transition-colors">{comic.category}</Link></li>
                <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
                <li className="text-foreground-950 font-medium">{comic.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="shrink-0 mx-auto md:mx-0">
                <div className="w-48 sm:w-56 md:w-64 rounded-2xl overflow-hidden shadow-sm">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫大全`}
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {comic.category}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-700 text-xs font-medium">
                    <i className="ri-star-fill"></i>
                    {comic.rating}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-1">作者：{comic.author}</p>
                <p className="text-sm text-foreground-600 mb-4">最新：{comic.latestChapter} · 更新于 {comic.updateDate}</p>

                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-foreground-400"></i>
                    {(comic.views / 10000).toFixed(1)}万阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line text-foreground-400"></i>
                    {comic.favorites.toLocaleString()}收藏
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-book-read-line mr-1.5"></i>
                    开始阅读
                  </button>
                  <button
                    onClick={() => setFavorited(!favorited)}
                    className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border ${
                      favorited
                        ? 'bg-accent-500 text-background-50 border-accent-500 hover:bg-accent-600'
                        : 'bg-background-50 text-foreground-700 border-background-200 hover:border-accent-300 hover:text-accent-600'
                    }`}
                  >
                    <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} mr-1.5`}></i>
                    {favorited ? '已收藏' : '加入收藏'}
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-share-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs + Content */}
        <section className="py-6 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="px-4 md:px-6 flex items-center gap-1">
            <button
              onClick={() => setActiveTab('chapters')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'chapters'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              <i className="ri-list-check mr-1.5"></i>
              章节列表 ({comic.chapters.length})
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'comments'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              <i className="ri-chat-3-line mr-1.5"></i>
              读者评论 ({comic.comments.length})
            </button>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl">
            {activeTab === 'chapters' ? (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">章节列表</h2>
                  <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
                </div>
                {comic.chapters.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {comic.chapters.map((chapter, index) => (
                      <button
                        key={chapter.number}
                        className="flex items-center justify-between p-4 rounded-xl bg-background-100 hover:bg-background-200/70 border border-transparent hover:border-background-200 transition-all cursor-pointer text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold shrink-0 ${
                            index < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                          }`}>
                            {chapter.number}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-950 truncate">{chapter.title}</p>
                            <p className="text-xs text-foreground-500">{chapter.date}</p>
                          </div>
                        </div>
                        <i className="ri-arrow-right-line text-foreground-400 shrink-0 ml-2"></i>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500 py-10 text-center">暂无章节数据</p>
                )}
              </div>
            ) : (
              <div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">读者评论</h2>
                {comic.comments.length > 0 ? (
                  <div className="space-y-4">
                    {comic.comments.map((comment) => (
                      <div key={comment.id} className="flex gap-4 p-4 rounded-xl bg-background-100">
                        <img
                          src={comment.avatar}
                          alt={comment.user}
                          className="w-10 h-10 rounded-full object-cover shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-foreground-950">{comment.user}</span>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <i key={i} className={i < comment.rating ? 'ri-star-fill text-xs text-yellow-400' : 'ri-star-line text-xs text-foreground-300'}></i>
                              ))}
                            </div>
                            <span className="text-xs text-foreground-500">{comment.date}</span>
                          </div>
                          <p className="text-sm text-foreground-700 mt-2 leading-relaxed">{comment.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-sm text-foreground-500 mb-4">暂无评论，来发表第一条评论吧！</p>
                    <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
                      写评论
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Similar Comics */}
        {similarComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {similarComics.map((similar) => (
                  <Link key={similar.id} to={`/comic/${similar.id}`} className="group cursor-pointer">
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={similar.coverImage}
                        alt={similar.title}
                        title={`${similar.title} - 韩漫大全`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2">
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          {similar.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading font-bold text-foreground-950 text-sm truncate group-hover:text-primary-600 transition-colors">
                      {similar.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{similar.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}